import type { ReactNode, Ref } from "react";
import { Children } from "react";
import { Box, SimpleGrid, type SimpleGridProps } from "@mantine/core";
import { splitAfterFirstRow } from "@/pages/discover/splitAfterFirstRow.util";
import { PageCtaSlotList } from "@/components/shared/PageCtaSlotList";
import { PAGE_CTA_SLOT_AFTER_FIRST_ROW, type PageCtaPageKey } from "@shared/pageCtaSlots";
import CorridorFixedCardGrid from "@/pages/ecosystem-heads/components/CorridorFixedCardGrid";
import { ECOSYSTEM_EXPLORE_CARD_GRID_GAP } from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";

export function PageCtaAfterFirstRow({
  pageKey,
}: {
  pageKey: Extract<PageCtaPageKey, "DISCOVER" | "EXPERTS" | "CORRIDORS">;
}) {
  return (
    <Box my="md" w="100%">
      <PageCtaSlotList pageKey={pageKey} slotKey={PAGE_CTA_SLOT_AFTER_FIRST_ROW} />
    </Box>
  );
}

const SPACING_PX = { sm: 12, md: 16, lg: 24 } as const;

export function ExploreGridWithAfterFirstRowCta({
  pageKey,
  columns,
  gridRef,
  cols,
  spacing = "sm",
  verticalSpacing,
  /** `media` = viewport named breakpoints; `container` = pixel keys on grid width. */
  colsType = "container",
  /** Corridor explore: fluid equal columns (up to 5), expo-style stretch. */
  fixedCorridorCards = false,
  children,
}: {
  pageKey: Extract<PageCtaPageKey, "EXPERTS" | "CORRIDORS">;
  columns: number;
  gridRef?: Ref<HTMLDivElement>;
  cols: SimpleGridProps["cols"];
  spacing?: "sm" | "md" | "lg";
  verticalSpacing?: "sm" | "md" | "lg";
  colsType?: "container" | "media";
  fixedCorridorCards?: boolean;
  children: ReactNode;
}) {
  const { firstRow, rest } = splitAfterFirstRow(Children.toArray(children), columns);
  const useFixed = fixedCorridorCards || pageKey === "CORRIDORS";
  const gap = SPACING_PX[spacing] ?? ECOSYSTEM_EXPLORE_CARD_GRID_GAP;
  const rowGap = verticalSpacing ? SPACING_PX[verticalSpacing] : gap;

  if (useFixed) {
    return (
      <Box ref={gridRef} w="100%">
        <CorridorFixedCardGrid gap={gap} verticalGap={rowGap}>
          {firstRow}
        </CorridorFixedCardGrid>
        <PageCtaAfterFirstRow pageKey={pageKey} />
        {rest.length > 0 ? (
          <CorridorFixedCardGrid gap={gap} verticalGap={rowGap}>
            {rest}
          </CorridorFixedCardGrid>
        ) : null}
      </Box>
    );
  }

  const gridProps =
    colsType === "media"
      ? {
          cols,
          spacing,
          verticalSpacing: verticalSpacing ?? spacing,
          w: "100%" as const,
          styles: { root: { alignItems: "stretch" } },
        }
      : {
          type: "container" as const,
          cols,
          spacing,
          verticalSpacing: verticalSpacing ?? spacing,
          w: "100%" as const,
          styles: { container: { alignItems: "stretch" } },
        };

  return (
    <Box ref={gridRef} w="100%">
      <SimpleGrid {...gridProps}>{firstRow}</SimpleGrid>
      <PageCtaAfterFirstRow pageKey={pageKey} />
      {rest.length > 0 ? <SimpleGrid {...gridProps}>{rest}</SimpleGrid> : null}
    </Box>
  );
}
